// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyWarranty, TyWarrantyRecord, TyWarrantyState } from "@/types";
import { formatDate, pressToOpen, readLangValue, warrantyDaysLeft, warrantyElapsed, warrantyState } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    warranty: ({ kind: "template" } & TyWarranty) | ({ kind: "record" } & TyWarrantyRecord);
    /**
     * Which screen it is being read on, and therefore **which half of the pair to print**. A unit's
     * page already says which flat, so the useful line is who holds the cover; a person's page
     * already says who, so the useful line is which flat it is on.
     */
    place: "unit" | "customer";
    onOpen?: () => void;
};

const TONES: Record<TyWarrantyState, "available" | "reserved" | "neutral" | "danger"> = {
    notStarted: "neutral",
    active: "available",
    expiring: "reserved",
    expired: "danger",
    unknown: "neutral",
};

/**
 * One guarantee, before or after the sale.
 *
 * **The same card draws both**, which is the point: what a unit comes with and what a buyer holds
 * are the same thing at two moments, and only the clock differs. A template reads `notStarted` and
 * prints no end date, rather than being a different list in a different shape on a different screen.
 *
 * **Two facts, a state and the clock, and nothing else.** Who stands behind it and when it runs out
 * are what somebody scanning a list of these is deciding on; the duration, the start, the phone
 * number and the paperwork are what they want *after* they have picked one, and those live in the
 * popup the whole card opens. A card that carried its own buttons would be offering four actions on
 * a row that is really one question — which of these needs me today.
 *
 * **The rail is what answers that question.** "Ends 14/03/2027" and "still covered" are the same
 * fact twice, and neither says how much of the cover is left — a date and a duration are arithmetic
 * somebody does in their head once and then stops doing. The bar is the answer already worked out,
 * which is why the card is wide enough to carry its two facts on one line and leave room for it.
 */
export default function WarrantyCard({ warranty, place, onOpen }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const isRecord = warranty.kind === "record";
    const state: TyWarrantyState = isRecord ? warrantyState(warranty) : "notStarted";

    /** A template has no clock running on it, so it draws no rail — there is nothing to be along. */
    const elapsed = isRecord ? warrantyElapsed(warranty) : null;
    const daysLeft = isRecord ? warrantyDaysLeft(warranty) : null;

    const unit = isRecord && typeof warranty.unit === "object" && warranty.unit ? warranty.unit : null;
    const project = unit && typeof unit.project === "object" && unit.project ? readLangValue(unit.project.name, lang) : null;
    const customer = isRecord && typeof warranty.customer === "object" && warranty.customer ? warranty.customer : null;

    /** The other half of the pair: the holder on a unit's page, the unit on a person's. */
    const counterpart =
        place === "unit"
            ? customer && { icon: "team" as const, label: t.warranties.holder, value: customer.name }
            : unit && { icon: "properties" as const, label: t.warranties.under, value: project ? `${project} · ${unit.number}` : unit.number };

    return (
        <article
            className={`warranty-card warranty-card--${state}`}
            role={onOpen ? "button" : undefined}
            tabIndex={onOpen ? 0 : undefined}
            onClick={onOpen}
            onKeyDown={onOpen ? pressToOpen(onOpen) : undefined}
        >
            <div className="warranty-card__top">
                <span className="warranty-card__mark" aria-hidden="true">
                    <Icon name="crown" size={16} />
                </span>

                <div className="warranty-card__identity">
                    <span className="warranty-card__title">{t.properties.warrantyTypes[warranty.type]}</span>
                    {counterpart && (
                        <span className="warranty-card__where">
                            <Icon name={counterpart.icon} size={12} />
                            {counterpart.value}
                        </span>
                    )}
                </div>

                <StatusChip tone={TONES[state]} size="sm" label={t.warranties.state[state]} />
            </div>

            {/*
             * Two chips rather than two columns of small print. They are read at a glance off a grid
             * of these, and a badge is what tells a name from a date before either is read — the
             * same argument the holder card's figures make.
             */}
            <dl className="warranty-card__facts">
                <div className="warranty-card__fact">
                    <span className="warranty-card__fact-icon" aria-hidden="true">
                        <Icon name="team" size={14} />
                    </span>
                    <dt>{t.warranties.provider}</dt>
                    <dd>{warranty.provider?.name || t.properties.notSet}</dd>
                </div>

                <div className="warranty-card__fact">
                    <span className="warranty-card__fact-icon" aria-hidden="true">
                        <Icon name="calendar" size={14} />
                    </span>
                    <dt>{t.warranties.endsAt}</dt>
                    {/* A template has no end date to print: the clock has not started on it. */}
                    <dd>
                        <bdi dir="ltr">{isRecord && warranty.endsAt ? formatDate(warranty.endsAt, lang) : t.properties.notSet}</bdi>
                    </dd>
                </div>
            </dl>

            {elapsed !== null && (
                <div className="warranty-card__track">
                    <span
                        className="warranty-card__rail"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(elapsed * 100)}
                        aria-label={t.warranties.timeline}
                    >
                        <span className="warranty-card__rail-fill" style={{ inlineSize: `${Math.round(elapsed * 100)}%` }} />
                    </span>
                    <small className="warranty-card__left">
                        {daysLeft !== null && daysLeft > 0 ? t.warranties.daysLeft.replace("{days}", String(daysLeft)) : t.warranties.state.expired}
                    </small>
                </div>
            )}
        </article>
    );
}
